import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface Bridge {
  id: string;
  name: string;
  version: string | null;
  queue_depth: number;
  last_seen_at: string | null;
  last_replay_at: string | null;
  last_replay_ok: number;
  last_replay_fehler: number;
  created_at: string;
}

/** Online, wenn das letzte Lebenszeichen weniger als zwei Minuten her ist. */
function istOnline(b: Bridge): boolean {
  if (!b.last_seen_at) return false;
  return Date.now() - new Date(b.last_seen_at).getTime() < 120_000;
}

export function BridgesPage() {
  const [bridges, setBridges] = useState<Bridge[] | null>(null);
  const [name, setName] = useState('');
  const [neuerToken, setNeuerToken] = useState<{ name: string; token: string } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      setBridges(await api<Bridge[]>('/api/bridges'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
    const t = setInterval(() => void laden(), 30_000);
    return () => clearInterval(t);
  }, [laden]);

  async function anlegen() {
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const r = await api<{ bridge: Bridge; token: string }>('/api/bridges', {
        method: 'POST',
        body: { name: name.trim() },
      });
      setNeuerToken({ name: r.bridge.name, token: r.token });
      setName('');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function loeschen(b: Bridge) {
    if (!confirm(`Bridge „${b.name}" wirklich entfernen? Der Token wird ungültig.`)) return;
    setBusy(true);
    try {
      await api(`/api/bridges/${b.id}`, { method: 'DELETE' });
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card title="Offline-Bridge">
        <p className="small muted" style={{ marginTop: 0 }}>
          Eine Bridge ist die Vor-Ort-Box (Synology-Container oder Linux-Mini-PC), die den
          Hallenbetrieb bei Leitungsausfall weiterträgt. Sie meldet sich mit einem eigenen Token an
          und schickt regelmäßig ein Lebenszeichen. In dieser Ausbaustufe zieht sie einen Lese-
          Snapshot (Artikel, Bestand, Lagerplätze) und bedient Bestandsabfragen auch offline.
        </p>
      </Card>

      {neuerToken && (
        <Card title="Neuer Bridge-Token">
          <Notice kind="info">
            Der Token für „{neuerToken.name}" wird <strong>nur jetzt</strong> angezeigt. In der
            Konfiguration der Box als <code>BRIDGE_TOKEN</code> hinterlegen.
          </Notice>
          <pre
            style={{
              background: 'var(--code-bg, #1e1e1e)',
              color: '#eee',
              padding: 12,
              borderRadius: 6,
              overflowX: 'auto',
              userSelect: 'all',
            }}
          >
            {neuerToken.token}
          </pre>
          <button onClick={() => setNeuerToken(null)}>Verstanden, ausblenden</button>
        </Card>
      )}

      <Card
        title="Bridges"
        padded={false}
        actions={
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <TextField label="" value={name} onChange={setName} placeholder="Name, z. B. Lager Dresden" />
            <button className="primary small" onClick={anlegen} disabled={busy || !name.trim()}>
              Bridge anlegen
            </button>
          </span>
        }
      >
        {!bridges ? (
          <Loading />
        ) : bridges.length === 0 ? (
          <Empty>Noch keine Bridge angelegt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Version</th>
                <th className="num">Warteschlange</th>
                <th>Letztes Lebenszeichen</th>
                <th>Letzter Abgleich</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bridges.map((b) => (
                <tr key={b.id}>
                  <td>{b.name}</td>
                  <td>
                    {istOnline(b) ? (
                      <Badge kind="ok">online</Badge>
                    ) : (
                      <Badge kind={b.last_seen_at ? 'bad' : 'default'}>
                        {b.last_seen_at ? 'offline' : 'nie verbunden'}
                      </Badge>
                    )}
                  </td>
                  <td className="small muted">{b.version ?? '—'}</td>
                  <td className="num">{b.queue_depth}</td>
                  <td className="small muted">
                    {b.last_seen_at ? formatDateTime(b.last_seen_at) : '—'}
                  </td>
                  <td className="small muted">
                    {b.last_replay_at ? (
                      <>
                        {formatDateTime(b.last_replay_at)} · {b.last_replay_ok} ok
                        {b.last_replay_fehler > 0 && (
                          <>
                            {' '}
                            <Badge kind="bad">{b.last_replay_fehler} Fehler</Badge>
                          </>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => loeschen(b)} disabled={busy}>
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
